import { useEffect, useState } from "react";
import { Plus, Trash2, NotebookPen, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function DraftpadPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ title: "", body: "" });

  const load = async () => { const { data } = await api.get("/draftpad"); setItems(data); };
  useEffect(() => { load(); }, []);

  const newNote = () => { setActive(null); setForm({ title: "Untitled", body: "" }); };
  const select = (n) => { setActive(n); setForm({ title: n.title, body: n.body }); };

  const save = async () => {
    if (active) {
      await api.put(`/draftpad/${active.id}`, form);
      toast.success("Saved");
    } else {
      const { data } = await api.post("/draftpad", form);
      setActive(data); toast.success("Saved");
    }
    load();
  };
  const remove = async (id) => { await api.delete(`/draftpad/${id}`); if (active?.id === id) { setActive(null); setForm({ title: "", body: "" }); } load(); };

  return (
    <div className="px-5 lg:px-10 py-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy">Draftpad</h1>
          <p className="text-sm text-brand-muted mt-1">A quick space to capture rough thoughts before turning them into polished drafts.</p>
        </div>
        <button data-testid="draftpad-new-btn" onClick={newNote} className="btn-primary-gradient rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1"><Plus className="w-4 h-4" />New note</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <aside className="bg-white rounded-2xl border border-brand-borderc p-3 max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="text-center py-10 text-brand-muted text-sm">
              <NotebookPen className="w-8 h-8 mx-auto text-brand-purple" />
              <div className="mt-2 font-medium text-brand-text">Your Draftpad is empty</div>
              <div className="mt-1 text-xs">Capture a thought — turn it into a draft later.</div>
            </div>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => select(n)} className={`w-full text-left p-3 rounded-xl mb-1 ${active?.id === n.id ? "bg-brand-surface" : "hover:bg-brand-surface/50"}`}>
              <div className="font-semibold text-brand-navy truncate">{n.title || "Untitled"}</div>
              <div className="text-xs text-brand-muted truncate">{n.body}</div>
            </button>
          ))}
        </aside>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-brand-borderc p-5">
          <input data-testid="draftpad-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="w-full px-3 py-2.5 text-lg font-semibold bg-transparent outline-none border-b border-brand-borderc focus:border-brand-purple" />
          <textarea data-testid="draftpad-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={14} placeholder="Start typing… ideas, lists, anything." className="mt-3 w-full p-3 rounded-xl border border-brand-borderc bg-brand-bg text-sm focus:border-brand-purple outline-none resize-none" />
          <div className="mt-3 flex justify-between">
            <button data-testid="draftpad-save-btn" onClick={save} className="btn-primary-gradient rounded-full px-5 py-2 text-sm font-semibold inline-flex items-center gap-1"><Save className="w-4 h-4" />Save</button>
            {active && <button onClick={() => remove(active.id)} className="text-sm text-red-500 inline-flex items-center gap-1"><Trash2 className="w-4 h-4" />Delete</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
